import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ui from "../../pages/dashboard/DashboardUI.module.css";
import useClickOutside from "../../hooks/useClickOutside";
import { getInitials, avatarColorFromName } from "../../utils/avatarUtils";

const loadColumnWidths = (storageKey) => {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(`${storageKey}:widths`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveColumnWidths = (storageKey, widths) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(`${storageKey}:widths`, JSON.stringify(widths));
  } catch {
    // Storage unavailable (private mode, quota) — widths just don't persist.
  }
};

// Render avatar stack component
const AvatarStack = ({ assignees = [], totalCount = 0, maxDisplay = 3 }) => {
  if (!assignees || assignees.length === 0) return null;
  
  const displayedAssignees = assignees.slice(0, maxDisplay);
  const remaining = totalCount - maxDisplay;
  
  return (
    <div className={ui.avatarStack}>
      {displayedAssignees.map((assignee, index) => (
        <div
          key={assignee.employee_id ?? assignee.user_id ?? `${assignee.user_name}-${index}`}
          className={ui.avatar}
          style={{ 
            backgroundColor: avatarColorFromName(assignee.user_name),
            zIndex: maxDisplay - index
          }}
          title={assignee.user_name}
        >
          {getInitials(assignee.user_name)}
        </div>
      ))}
      {remaining > 0 && (
        <div className={`${ui.avatar} ${ui.avatarMore}`}>
          +{remaining}
        </div>
      )}
    </div>
  );
};

export default function DataTable({
  expandedRows = {},
  toggleRowExpand = () => {},
  columns = [],
  data = [],
  onRowClick = () => {},
  groupedBy = null,
  collapsedGroups = {},
  toggleGroupCollapse = () => {},
  customCellRender = {},
  groupOrder = [],
  renderExpandedRow = null,
  sortColumn = null,
  sortDirection = "asc",
  onSort = null,
  onAddDetail = null,
  onToggleColumn = null,
  onMoveColumn = null,
  getGroupColor = null,
  selectable = false,
  selectedIds = null,
  onToggleRow = () => {},
  onToggleGroup = () => {},
  onToggleAll = () => {},
  getRowId = (row) => row.project_id ?? row.lead_id ?? row.id,
  renderGroupExtra = null,
  storageKey = null,
  // Totals row under the body: { [columnKey]: node }, drawn as is.
  footer = null,
  // Show this many rows, then scroll inside the table with the header (and totals) pinned.
  maxRows = null,
}) {
  const visibleColumns = columns.filter((col) => col.visible);

  // The trailing "+" header is a real column as far as the table is concerned,
  // so rows and group headers have to account for it or the grid goes ragged.
  const hasColumnMenu = Boolean(onAddDetail || onToggleColumn);
  const totalColumnCount = visibleColumns.length + (hasColumnMenu ? 1 : 0) + (selectable ? 1 : 0);

  // Fixed-positioned from the button because the table wrapper scrolls and would clip it.
  const [columnMenuPos, setColumnMenuPos] = useState(null);
  const columnMenuRef = useRef(null);
  const closeColumnMenu = useCallback(() => setColumnMenuPos(null), []);
  useClickOutside(columnMenuRef, closeColumnMenu, Boolean(columnMenuPos));
  const toggleColumnMenu = (e) => {
    if (columnMenuPos) return setColumnMenuPos(null);
    const rect = e.currentTarget.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const below = window.innerHeight - rect.bottom;
    // A + low on screen opens the menu upward, so it is never pushed off the bottom where it can't be scrolled to.
    setColumnMenuPos(below < 360 && rect.top > below
      ? { bottom: window.innerHeight - rect.top + 6, right, maxHeight: Math.min(360, rect.top - 14) }
      : { top: rect.bottom + 6, right, maxHeight: Math.min(360, below - 14) });
  };

  // Header drag-to-reorder. The resize handle's mousedown preventDefault stops it starting a drag.
  const [dragKey, setDragKey] = useState(null);
  const [dropKey, setDropKey] = useState(null);
  const selected = selectedIds || new Set();

  // Column widths: draggable per column, persisted under storageKey when the
  // caller passes one. table-layout: fixed (DashboardUI.module.css) means a
  // <col> width is what actually controls rendering — the th/td never need it.
  //
  // widthsRef mirrors `widths` but is written synchronously at every change
  // (not via a useEffect), so drag handlers always read the current map
  // instead of a stale pre-render snapshot — mouseup can fire before React
  // flushes the state update from the last mousemove.
  const [widths, setWidths] = useState(() => loadColumnWidths(storageKey));
  const widthsRef = useRef(widths);
  const resizingRef = useRef(null);

  const updateWidths = (next) => {
    widthsRef.current = next;
    setWidths(next);
  };

  const resetWidth = (colKey) => {
    const { [colKey]: _removed, ...rest } = widthsRef.current;
    updateWidths(rest);
    saveColumnWidths(storageKey, rest);
  };

  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;

    // Pin every column's own rendered width first. Otherwise, with only the
    // dragged column's width set explicitly, table-layout: fixed quietly
    // shrinks the *other* unsized columns to keep the table's total width
    // unchanged — which drags this column's left edge along with it, so
    // pulling the right edge right looked like the column growing from its
    // center instead of just the edge you're holding moving.
    const pinned = { ...widthsRef.current };
    const table = th.closest("table");
    table.querySelectorAll("th[data-col-key]").forEach((cellTh) => {
      const key = cellTh.dataset.colKey;
      if (pinned[key] === undefined) pinned[key] = cellTh.offsetWidth;
    });
    updateWidths(pinned);

    resizingRef.current = {
      key: colKey,
      startX: e.clientX,
      startWidth: pinned[colKey] || th.offsetWidth,
      lastWidth: pinned[colKey] || th.offsetWidth,
    };
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  };

  const handleResizeMove = (e) => {
    const r = resizingRef.current;
    if (!r) return;
    const next = Math.max(60, r.startWidth + (e.clientX - r.startX));
    r.lastWidth = next;
    updateWidths({ ...widthsRef.current, [r.key]: next });
  };

  const handleResizeEnd = () => {
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
    const r = resizingRef.current;
    resizingRef.current = null;
    if (!r) return;
    saveColumnWidths(storageKey, widthsRef.current);
  };

  // Rows differ in height (two-line cells), so the cap is measured from the real rows after each render.
  const wrapperRef = useRef(null);
  const [capHeight, setCapHeight] = useState(null);
  useLayoutEffect(() => {
    const wrap = wrapperRef.current;
    const rows = maxRows && wrap ? wrap.querySelectorAll("tbody > tr") : [];
    const next = rows.length > maxRows
      ? Math.ceil(rows[maxRows - 1].getBoundingClientRect().bottom - wrap.getBoundingClientRect().top + wrap.scrollTop + (wrap.querySelector("tfoot")?.offsetHeight || 0))
      : null;
    setCapHeight((prev) => (prev === next ? prev : next));
  }, [maxRows, data, columns, footer, widths]);

  const getUrgencyClass = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'high': return ui.urgencyHigh;
      case 'medium': return ui.urgencyMedium;
      case 'low': return ui.urgencyLow;
      default: return '';
    }
  };

  const renderRow = (row, keyPrefix = "") => {
    const rowId = getRowId(row);
    const rowKey = keyPrefix + rowId;
    const isExpanded = expandedRows[row.project_id];
    const hasMilestones = row.milestones && row.milestones.length > 0;

    return (
      <React.Fragment key={rowKey}>
        <tr
          onClick={() => onRowClick(row)}
          className={ui.projectRow}
        >
          {selectable && (
            <td className={ui.selectCell} onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selected.has(rowId)}
                onChange={() => onToggleRow(rowId)}
              />
            </td>
          )}
          {visibleColumns.map((col, colIndex) => {
            if (col.key === "client_company") {
              return (
                <td key={col.key} className={ui.projectNameCell}>
                  <div className={ui.projectNameWrapper}>
                    {hasMilestones && (
                      <button
                        type="button"
                        className={ui.expandBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpand(row.project_id);
                        }}
                      >
                        <span className={`${ui.expandIcon} ${isExpanded ? ui.expanded : ''}`}>
                          ›
                        </span>
                      </button>
                    )}
                    {!hasMilestones && <span className={ui.expandPlaceholder} />}
                    {(row.display_color || row.status_color || row.action_color) && (
                      <span 
                        className={ui.projectColorDot} 
                        style={{ backgroundColor: row.display_color || row.status_color || row.action_color }}
                        title={row.display_color ? "Lead color indicator" : (row.status_color ? "Project status color" : "Lead action color")}
                      />
                    )}
                    <span className={ui.projectNameText}>{row[col.key] || "—"}</span>
                  </div>
                </td>
              );
            }

            if (col.key === "assignees") {
              return (
                <td key={col.key} className={ui.assigneesCell}>
                  <AvatarStack 
                    assignees={row.assignees} 
                    totalCount={row.assignee_count || 0} 
                    maxDisplay={4} 
                  />
                </td>
              );
            }

            if (col.key === "urgency" && !customCellRender[col.key]) {
              return (
                <td key={col.key}>
                  <span className={`${ui.urgencyPill} ${getUrgencyClass(row.urgency)}`}>
                    {row.urgency || "—"}
                  </span>
                </td>
              );
            }

            return (
              <td key={col.key} className={col.key === 'status' ? ui.statusCell : ''} style={col.align ? { textAlign: col.align } : undefined}>
                {customCellRender[col.key]
                  ? customCellRender[col.key](row)
                  : row[col.key] || "—"}
              </td>
            );
          })}
          {hasColumnMenu && <td className={ui.addColumnCell} />}
        </tr>

        {isExpanded && hasMilestones && renderExpandedRow && (
          <tr className={ui.milestoneExpandedRow}>
            <td colSpan={totalColumnCount} className={ui.milestoneExpandedCell}>
              {renderExpandedRow(row)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };


  const renderGroupedRows = () => {
    const grouped = data.reduce((acc, row) => {
    const groupKey =
      typeof groupedBy === "function" ? groupedBy(row) : row[groupedBy] || "Other";

    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(row);
    return acc;
  }, {});


    let entries = Object.entries(grouped);

    if (groupOrder.length > 0) {
      entries = entries.sort(
        ([a], [b]) => groupOrder.indexOf(a) - groupOrder.indexOf(b)
      );
    }

    return entries.map(([group, rows]) => {
      const isCollapsed = collapsedGroups[group] || false;

      const groupRowIds = rows.map((row) => getRowId(row));
      const allGroupSelected = groupRowIds.length > 0 && groupRowIds.every((id) => selected.has(id));

      return (
        <React.Fragment key={group}>
          <tr
            className={ui.groupHeaderRow}
            onClick={() => toggleGroupCollapse(group)}
          >
            <td colSpan={totalColumnCount}>
              <div className={ui.groupHeader}>
                {selectable && (
                  <input
                    type="checkbox"
                    checked={allGroupSelected}
                    onChange={() => onToggleGroup(groupRowIds, !allGroupSelected)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <span className={`${ui.groupExpandIcon} ${isCollapsed ? '' : ui.expanded}`}>
                  ›
                </span>
                {getGroupColor && (
                  <span className={ui.groupColorDot} style={{ backgroundColor: getGroupColor(group, rows) }} />
                )}
                <span className={ui.groupName}>{group}</span>
                <span className={ui.groupCount}>{rows.length}</span>
                {renderGroupExtra && renderGroupExtra(rows)}
              </div>
            </td>
          </tr>
          {!isCollapsed && rows.map((row) => renderRow(row, group + "-"))}
        </React.Fragment>
      );
    });
  };

  return (
    <div ref={wrapperRef} className={ui.tableWrapper} style={capHeight ? { maxHeight: capHeight, overflowY: "auto" } : undefined}>
      <table className={`${ui.projectTable} ${capHeight ? ui.pinnedEdges : ""}`}>
        <colgroup>
          {selectable && <col style={{ width: '32px' }} />}
          {visibleColumns.map((col) => (
            <col key={col.key} style={widths[col.key] ? { width: `${widths[col.key]}px` } : undefined} />
          ))}
          {hasColumnMenu && <col style={{ width: '40px' }} />}
        </colgroup>
        <thead>
          <tr>
            {selectable && (
              <th className={ui.selectCell}>
                <input
                  type="checkbox"
                  checked={data.length > 0 && data.every((row) => selected.has(getRowId(row)))}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </th>
            )}
            {visibleColumns.map((col) => (
              <th
                key={col.key}
                data-col-key={col.key}
                onClick={onSort ? () => onSort(col.key) : undefined}
                className={`${onSort ? ui.sortableHeader : ''} ${dropKey === col.key && dragKey !== col.key ? ui.columnDropTarget : ''}`}
                style={{ position: capHeight ? undefined : 'relative', cursor: onSort ? 'pointer' : undefined, textAlign: col.align }}
                draggable={Boolean(onMoveColumn)}
                onDragStart={onMoveColumn ? (e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(col.key); } : undefined}
                onDragOver={onMoveColumn ? (e) => { e.preventDefault(); setDropKey(col.key); } : undefined}
                onDragEnd={onMoveColumn ? () => { setDragKey(null); setDropKey(null); } : undefined}
                onDrop={onMoveColumn ? (e) => {
                  e.preventDefault();
                  if (dragKey) onMoveColumn(dragKey, col.key);
                  setDragKey(null);
                  setDropKey(null);
                } : undefined}
              >
                <span className={ui.headerContent}>
                  {col.label}
                  {onSort && sortColumn === col.key && (
                    <span className={ui.sortIndicator}>
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </span>
                <div
                  className={ui.colResizeHandle}
                  onMouseDown={(e) => handleResizeStart(e, col.key)}
                  onDoubleClick={() => resetWidth(col.key)}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
            ))}
            {hasColumnMenu && (
              <th className={ui.addColumnHeader}>
                <div ref={columnMenuRef} className={ui.columnMenuWrap}>
                  <button
                    type="button"
                    className={ui.addColumnButton}
                    onClick={toggleColumnMenu}
                    title="Columns"
                    aria-label="Columns"
                    aria-expanded={Boolean(columnMenuPos)}
                  >
                    +
                  </button>
                  {columnMenuPos && ReactDOM.createPortal(
                    <div className={ui.columnMenu} style={columnMenuPos} role="menu" onMouseDown={(e) => e.stopPropagation()}>
                      {onAddDetail && (
                        <button
                          type="button"
                          className={ui.columnMenuAdd}
                          onClick={() => { setColumnMenuPos(null); onAddDetail(); }}
                        >
                          + Add a detail
                        </button>
                      )}
                      {onToggleColumn && columns.map((col) => (
                        <label
                          key={col.key}
                          className={`${ui.columnMenuOption} ${dropKey === col.key && dragKey !== col.key ? ui.columnMenuDrop : ''}`}
                          draggable={Boolean(onMoveColumn)}
                          onDragStart={onMoveColumn ? (e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(col.key); } : undefined}
                          onDragOver={onMoveColumn ? (e) => { e.preventDefault(); setDropKey(col.key); } : undefined}
                          onDragEnd={onMoveColumn ? () => { setDragKey(null); setDropKey(null); } : undefined}
                          onDrop={onMoveColumn ? (e) => { e.preventDefault(); if (dragKey) onMoveColumn(dragKey, col.key); setDragKey(null); setDropKey(null); } : undefined}
                        >
                          {onMoveColumn && <span className={ui.columnMenuHandle} aria-hidden="true">⋮⋮</span>}
                          <input type="checkbox" checked={col.visible} onChange={() => onToggleColumn(col.key)} />
                          {col.label}
                        </label>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groupedBy ? renderGroupedRows() : data.map((row) => renderRow(row))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className={ui.footerRow}>
              {selectable && <td />}
              {visibleColumns.map((col) => (
                <td key={col.key} style={col.align ? { textAlign: col.align } : undefined}>{footer[col.key]}</td>
              ))}
              {hasColumnMenu && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}